/**
 * Usage Reports Effects
 * NgRx effects for usage reports side effects
 */
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, switchMap, withLatestFrom } from 'rxjs/operators';
import { UsageReportsService, USAGE_EXPORT_ERROR } from '../services/usage-reports.service';
import { NotificationService } from '@core/services/notification.service';
import * as UsageReportsActions from './usage-reports.actions';
import * as UsageReportsSelectors from './usage-reports.selectors';

@Injectable()
export class UsageReportsEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private usageReportsService = inject(UsageReportsService);
  private notificationService = inject(NotificationService);

  loadUsageStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsageReportsActions.loadUsageStats),
      switchMap(({ dateRange }) =>
        this.usageReportsService.getUsageStats(dateRange).pipe(
          map((stats) => UsageReportsActions.loadUsageStatsSuccess({ stats })),
          catchError((error) =>
            of(UsageReportsActions.loadUsageStatsFailure({ error: error.message }))
          )
        )
      )
    )
  );

  loadDailyUsage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsageReportsActions.loadDailyUsage),
      switchMap(({ dateRange }) =>
        this.usageReportsService.getDailyUsage(dateRange).pipe(
          map((dailyUsage) => UsageReportsActions.loadDailyUsageSuccess({ dailyUsage })),
          catchError((error) =>
            of(UsageReportsActions.loadDailyUsageFailure({ error: error.message }))
          )
        )
      )
    )
  );

  loadProcessingLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsageReportsActions.loadProcessingLogs),
      switchMap(({ filters, page, pageSize }) =>
        this.usageReportsService.getProcessingLogs(filters, page, pageSize).pipe(
          map(({ logs, total }) =>
            UsageReportsActions.loadProcessingLogsSuccess({ logs, total })
          ),
          catchError((error) =>
            of(UsageReportsActions.loadProcessingLogsFailure({ error: error.message }))
          )
        )
      )
    )
  );

  exportReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsageReportsActions.exportReport),
      withLatestFrom(this.store.select(UsageReportsSelectors.selectFilters)),
      switchMap(([{ format }, filters]) =>
        this.usageReportsService.exportReport(format, filters).pipe(
          map((blob) => {
            // Trigger download
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `usage-report.${format}`;
            a.click();
            window.URL.revokeObjectURL(url);
            return UsageReportsActions.exportReportSuccess();
          }),
          catchError(() => {
            this.notificationService.showError(USAGE_EXPORT_ERROR);
            return of(UsageReportsActions.exportReportFailure({ error: USAGE_EXPORT_ERROR }));
          })
        )
      )
    )
  );

  // Reload logs when filters or pagination changes
  reloadOnFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UsageReportsActions.updateFilters,
        UsageReportsActions.changePage,
        UsageReportsActions.changePageSize
      ),
      withLatestFrom(
        this.store.select(UsageReportsSelectors.selectFilters),
        this.store.select(UsageReportsSelectors.selectCurrentPage),
        this.store.select(UsageReportsSelectors.selectPageSize)
      ),
      map(([, filters, page, pageSize]) =>
        UsageReportsActions.loadProcessingLogs({ filters, page, pageSize })
      )
    )
  );
}

